import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Loader2, Activity, Server as ServerIcon, Eye, EyeOff,
  RefreshCw, Terminal, HardDrive, Cpu, Gauge, AlertTriangle, XCircle,
  FileText, Stethoscope, Power, Package, ChevronDown, ChevronUp, Rss,
} from 'lucide-react';
import { API, apiFetch } from '../apiClient';

type ServerRow = { id: string; label: string; host: string; port: number; username: string };
type ServerStatus = 'unknown' | 'online' | 'offline' | 'testing';

type MetricsLoad = { load1: number | null; load5: number | null; load15: number | null; runnable: number | null; totalProcs: number | null };
type MetricsMem = { total: number; used: number; free: number; available: number };
type DiskRow = { filesystem: string; fstype: string; size: string; used: string; avail: string; usePct: string; mounted: string };

type MetricsPayload = {
  load: MetricsLoad;
  cpus: number | null;
  memory: MetricsMem | null;
  disks: DiskRow[];
  uptime: string;
  uname: string;
};

interface ServerOpsPageProps {
  servers: ServerRow[];
  passwords: Record<string, string>;
  setPassword: (id: string, v: string) => void;
  showPw: Record<string, boolean>;
  setShowPw: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  serverStatus: Record<string, { status: ServerStatus; info: string }>;
  testConnection: (server: ServerRow) => void;
}

function formatBytes(n: number): string {
  if (!n || n < 0) return '—';
  const u = ['B', 'KB', 'MB', 'GB', 'TB'];
  let i = 0;
  let v = n;
  while (v >= 1024 && i < u.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v < 10 && i > 0 ? v.toFixed(1) : Math.round(v)} ${u[i]}`;
}

export default function ServerOpsPage({
  servers,
  passwords,
  setPassword,
  showPw,
  setShowPw,
  serverStatus,
  testConnection,
}: ServerOpsPageProps) {
  const [serverId, setServerId] = useState(servers[0]?.id || '');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshSec, setRefreshSec] = useState(5);
  const [metrics, setMetrics] = useState<MetricsPayload | null>(null);
  const [metricsErr, setMetricsErr] = useState<string | null>(null);
  const [metricsLoading, setMetricsLoading] = useState(false);
  const metricsSeq = useRef(0);

  const [logMode, setLogMode] = useState<'journal' | 'file'>('journal');
  const [journalUnit, setJournalUnit] = useState('');
  const [journalBoot, setJournalBoot] = useState(true);
  const [logLines, setLogLines] = useState(200);
  const [logPath, setLogPath] = useState('/var/log/syslog');
  const [logOut, setLogOut] = useState('');
  const [logLoading, setLogLoading] = useState(false);
  const [logErr, setLogErr] = useState<string | null>(null);
  const [liveLogs, setLiveLogs] = useState(false);
  const [liveLogSec, setLiveLogSec] = useState(3);
  const logScrollRef = useRef<HTMLDivElement>(null);

  const [diagOut, setDiagOut] = useState('');
  const [diagLoading, setDiagLoading] = useState(false);

  const [mgmtOpen, setMgmtOpen] = useState(false);
  const [mgmtOut, setMgmtOut] = useState('');
  const [mgmtLoading, setMgmtLoading] = useState(false);
  const [mgmtErr, setMgmtErr] = useState<string | null>(null);
  const [modal, setModal] = useState<'reboot' | 'apt_update' | 'apt_upgrade' | null>(null);
  const [confirmInput, setConfirmInput] = useState('');

  useEffect(() => {
    if (servers.length && !servers.some((s) => s.id === serverId)) setServerId(servers[0].id);
  }, [servers, serverId]);

  const pwPayload = useCallback(
    () => Object.fromEntries(Object.entries(passwords).filter(([, v]) => v.trim())),
    [passwords],
  );

  const post = useCallback(
    async (path: string, body: object) => {
      const r = await apiFetch(`${API}${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ passwords: pwPayload(), ...body }),
      });
      return r.json();
    },
    [pwPayload],
  );

  const fetchMetrics = useCallback(async () => {
    if (!serverId || !passwords[serverId]?.trim()) {
      setMetrics(null);
      setMetricsErr('Enter SSH password for this server.');
      return;
    }
    const seq = ++metricsSeq.current;
    setMetricsLoading(true);
    setMetricsErr(null);
    try {
      const r = await post(`/api/servers/${serverId}/metrics`, {});
      if (metricsSeq.current !== seq) return;
      if (r.error) {
        setMetrics(null);
        setMetricsErr(r.error);
        return;
      }
      setMetrics({
        load: r.load || {},
        cpus: r.cpus ?? null,
        memory: r.memory || null,
        disks: Array.isArray(r.disks) ? r.disks : [],
        uptime: r.uptime || '',
        uname: r.uname || '',
      });
    } catch (e: unknown) {
      if (metricsSeq.current === seq) {
        setMetrics(null);
        setMetricsErr(e instanceof Error ? e.message : String(e));
      }
    } finally {
      if (metricsSeq.current === seq) setMetricsLoading(false);
    }
  }, [post, serverId, passwords]);

  useEffect(() => {
    metricsSeq.current += 1;
    setMetrics(null);
    setMetricsErr(null);
    setLiveLogs(false);
  }, [serverId]);

  useEffect(() => {
    void fetchMetrics();
  }, [fetchMetrics]);

  useEffect(() => {
    if (!autoRefresh || !serverId || !passwords[serverId]?.trim()) return undefined;
    const ms = Math.min(120, Math.max(2, refreshSec)) * 1000;
    const id = window.setInterval(() => void fetchMetrics(), ms);
    return () => window.clearInterval(id);
  }, [autoRefresh, refreshSec, serverId, passwords, fetchMetrics]);

  const fetchLogs = useCallback(
    async (opts?: { silent?: boolean }) => {
      const silent = !!opts?.silent;
      if (!serverId || !passwords[serverId]?.trim()) {
        if (!silent) setLogErr('Password required');
        return;
      }
      if (!silent) setLogLoading(true);
      if (!silent) setLogErr(null);
      try {
        const body =
          logMode === 'journal'
            ? {
                mode: 'journal',
                lines: logLines,
                sinceBoot: journalBoot,
                unit: journalUnit.trim() || undefined,
              }
            : { mode: 'file', lines: logLines, filePath: logPath.trim() };
        const r = await post(`/api/servers/${serverId}/logs`, body);
        if (r.error) {
          setLogErr(r.error);
          if (!silent) setLogOut('');
          return;
        }
        setLogErr(null);
        setLogOut(r.output || '');
      } catch (e: unknown) {
        setLogErr(e instanceof Error ? e.message : String(e));
        if (!silent) setLogOut('');
      } finally {
        if (!silent) setLogLoading(false);
      }
    },
    [post, serverId, passwords, logMode, logLines, journalBoot, journalUnit, logPath],
  );

  useEffect(() => {
    if (!liveLogs || !serverId || !passwords[serverId]?.trim()) return undefined;
    const ms = Math.min(15, Math.max(2, liveLogSec)) * 1000;
    const id = window.setInterval(() => {
      void fetchLogs({ silent: true });
    }, ms);
    void fetchLogs({ silent: true });
    return () => window.clearInterval(id);
  }, [liveLogs, liveLogSec, fetchLogs, serverId, passwords]);

  useEffect(() => {
    if (!liveLogs || !logScrollRef.current) return;
    const el = logScrollRef.current;
    el.scrollTop = el.scrollHeight;
  }, [logOut, liveLogs]);

  const runDiagnostics = async () => {
    if (!serverId || !passwords[serverId]?.trim()) return;
    setDiagLoading(true);
    setDiagOut('');
    try {
      const r = await post(`/api/servers/${serverId}/diagnostics`, {});
      if (r.error) setDiagOut(r.error);
      else setDiagOut(r.output || '');
    } catch (e: unknown) {
      setDiagOut(e instanceof Error ? e.message : String(e));
    } finally {
      setDiagLoading(false);
    }
  };

  const runManage = async (action: string, confirm: string) => {
    if (!serverId || !passwords[serverId]?.trim()) return;
    setMgmtLoading(true);
    setMgmtErr(null);
    try {
      const r = await post(`/api/servers/${serverId}/manage`, { action, confirm });
      if (r.error) {
        setMgmtErr(r.error);
        setMgmtOut('');
        return;
      }
      setMgmtOut(r.output || (r.success ? 'Done.' : 'Command failed.'));
      if (!r.success) setMgmtErr(`Exit code ${r.code ?? '?'}`);
      setModal(null);
      setConfirmInput('');
      void fetchMetrics();
    } catch (e: unknown) {
      setMgmtErr(e instanceof Error ? e.message : String(e));
      setMgmtOut('');
    } finally {
      setMgmtLoading(false);
    }
  };

  const submitModal = () => {
    if (modal === 'reboot') {
      if (confirmInput !== 'REBOOT') {
        setMgmtErr('Type REBOOT exactly to confirm.');
        return;
      }
      void runManage('reboot', 'REBOOT');
    } else if (modal === 'apt_update') {
      if (confirmInput !== 'APT_UPDATE') {
        setMgmtErr('Type APT_UPDATE exactly to confirm.');
        return;
      }
      void runManage('apt_update', 'APT_UPDATE');
    } else if (modal === 'apt_upgrade') {
      if (confirmInput !== 'APT_UPGRADE') {
        setMgmtErr('Type APT_UPGRADE exactly to confirm.');
        return;
      }
      void runManage('apt_upgrade', 'APT_UPGRADE');
    }
  };

  const sv = servers.find((s) => s.id === serverId);
  const st = serverId ? serverStatus[serverId] : undefined;
  const pwOk = !!(serverId && passwords[serverId]?.trim());
  const memPct = metrics?.memory && metrics.memory.total > 0
    ? Math.round((metrics.memory.used / metrics.memory.total) * 100)
    : null;

  return (
    <div className="ops-page hub-outlet">
      <div className="dns-shell ops-shell">
        <main className="ops-main">
          <div className="hub-page-head">
            <h1 className="hub-page-h1">Servers</h1>
            <p className="hub-page-sub">Live metrics refresh, live log tail (poll), diagnostics, and gated maintenance over SSH</p>
          </div>
          <div className="ops-toolbar">
            <label className="ops-field">
              <span>Server</span>
              <select className="commit-select" value={serverId} onChange={(e) => setServerId(e.target.value)}>
                {servers.length === 0 && <option value="">No servers</option>}
                {servers.map((s) => (
                  <option key={s.id} value={s.id}>{s.label} — {s.host}</option>
                ))}
              </select>
            </label>
            {sv && (
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => testConnection(sv)}>Test SSH</button>
            )}
            {st?.status === 'online' && <span className="srv-status-tag online">Online</span>}
            {st?.status === 'offline' && <span className="srv-status-tag offline">Offline</span>}
            <label className="ops-field ops-pw">
              <span>Password</span>
              <input
                type={showPw[serverId] ? 'text' : 'password'}
                className="cfg-input"
                placeholder="SSH password"
                value={passwords[serverId] || ''}
                onChange={(e) => setPassword(serverId, e.target.value)}
                autoComplete="off"
              />
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => setShowPw((p) => ({ ...p, [serverId]: !p[serverId] }))}>
                {showPw[serverId] ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </label>
            <button type="button" className="btn btn-sm" disabled={!pwOk || metricsLoading} onClick={() => void fetchMetrics()}>
              {metricsLoading ? <Loader2 size={14} className="spin" /> : <RefreshCw size={14} />}
              Refresh now
            </button>
            <label className="ops-auto">
              <input type="checkbox" checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} />
              <span>Auto</span>
            </label>
            <label className="ops-field ops-interval">
              <span>Metrics every (s)</span>
              <input
                type="number"
                className="cfg-input ops-num"
                min={2}
                max={120}
                value={refreshSec}
                onChange={(e) => setRefreshSec(parseInt(e.target.value, 10) || 5)}
              />
            </label>
          </div>

          <section className="ops-section">
            <h2 className="ops-h2"><Gauge size={16} /> Load and usage</h2>
            {metricsErr && (
              <div className="nginx-msg nginx-msg-err ops-banner"><AlertTriangle size={12} /> {metricsErr}</div>
            )}
            {metrics && (
              <div className="ops-grid">
                <div className="ops-card">
                  <div className="ops-card-h"><Cpu size={14} /> CPU</div>
                  <div className="ops-metric">{metrics.cpus != null ? `${metrics.cpus} cores` : '—'}</div>
                  <div className="ops-sub">
                    Load {metrics.load?.load1 ?? '—'} / {metrics.load?.load5 ?? '—'} / {metrics.load?.load15 ?? '—'}
                  </div>
                  {metrics.load?.runnable != null && (
                    <div className="ops-tiny">Runnable: {metrics.load.runnable} / {metrics.load.totalProcs ?? '—'}</div>
                  )}
                </div>
                <div className="ops-card ops-card-wide">
                  <div className="ops-card-h"><Activity size={14} /> Memory</div>
                  {metrics.memory ? (
                    <>
                      <div className="ops-mem-bar-wrap">
                        <div className="ops-mem-bar" style={{ width: `${Math.min(100, memPct || 0)}%` }} />
                      </div>
                      <div className="ops-sub">
                        Used {formatBytes(metrics.memory.used)} of {formatBytes(metrics.memory.total)}
                        {' · '}
                        Avail {formatBytes(metrics.memory.available)}
                        {memPct != null ? ` (${memPct}%)` : ''}
                      </div>
                    </>
                  ) : (
                    <div className="ops-metric">—</div>
                  )}
                </div>
                <div className="ops-card ops-card-full">
                  <div className="ops-card-h"><HardDrive size={14} /> Disk</div>
                  <div className="ops-table-wrap">
                    <table className="ops-table">
                      <thead>
                        <tr>
                          <th>Mount</th>
                          <th>Type</th>
                          <th>Size</th>
                          <th>Used</th>
                          <th>Avail</th>
                          <th>Use</th>
                        </tr>
                      </thead>
                      <tbody>
                        {metrics.disks.length === 0 ? (
                          <tr><td colSpan={6} className="ops-muted">No disk rows</td></tr>
                        ) : (
                          metrics.disks.map((d, i) => (
                            <tr key={`${d.mounted}-${i}`}>
                              <td><code>{d.mounted}</code></td>
                              <td>{d.fstype || '—'}</td>
                              <td>{d.size}</td>
                              <td>{d.used}</td>
                              <td>{d.avail}</td>
                              <td>{d.usePct}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
                <div className="ops-card ops-card-full">
                  <div className="ops-card-h"><ServerIcon size={14} /> System</div>
                  <p className="ops-uname"><code>{metrics.uname}</code></p>
                  <pre className="ops-uptime">{metrics.uptime}</pre>
                </div>
              </div>
            )}
          </section>

          <section className="ops-section">
            <h2 className="ops-h2"><Terminal size={16} /> Logs</h2>
            <div className="ops-seg">
              <button type="button" className={logMode === 'journal' ? 'active' : ''} onClick={() => setLogMode('journal')}>Journal</button>
              <button type="button" className={logMode === 'file' ? 'active' : ''} onClick={() => setLogMode('file')}>File</button>
            </div>
            {logMode === 'journal' ? (
              <div className="ops-log-form">
                <label className="ops-field">
                  <span>Unit (optional)</span>
                  <input className="cfg-input mono" value={journalUnit} onChange={(e) => setJournalUnit(e.target.value)} placeholder="e.g. nginx.service — leave empty for all" spellCheck={false} />
                </label>
                <label className="ops-check">
                  <input type="checkbox" checked={journalBoot} onChange={(e) => setJournalBoot(e.target.checked)} />
                  <span>This boot only (when unit empty)</span>
                </label>
              </div>
            ) : (
              <div className="ops-log-form">
                <label className="ops-field">
                  <span>Path under /var/log/</span>
                  <input className="cfg-input mono" value={logPath} onChange={(e) => setLogPath(e.target.value)} spellCheck={false} />
                </label>
              </div>
            )}
            <label className="ops-field">
              <span>Lines</span>
              <input type="number" className="cfg-input ops-num" min={1} max={2000} value={logLines} onChange={(e) => setLogLines(parseInt(e.target.value, 10) || 200)} />
            </label>
            <div className="ops-log-actions">
              <button type="button" className="btn btn-sm btn-deploy" disabled={!pwOk || logLoading} onClick={() => void fetchLogs()}>
                {logLoading ? <Loader2 size={14} className="spin" /> : null}
                Fetch logs
              </button>
              <label className={`ops-live ${liveLogs ? 'on' : ''}`}>
                <input
                  type="checkbox"
                  checked={liveLogs}
                  onChange={(e) => setLiveLogs(e.target.checked)}
                  disabled={!pwOk}
                />
                <Rss size={14} className={liveLogs ? 'ops-live-pulse' : ''} />
                <span>Live tail</span>
              </label>
              <label className="ops-field ops-interval">
                <span>Live every (s)</span>
                <input
                  type="number"
                  className="cfg-input ops-num"
                  min={2}
                  max={15}
                  value={liveLogSec}
                  disabled={!liveLogs}
                  onChange={(e) => setLiveLogSec(parseInt(e.target.value, 10) || 3)}
                />
              </label>
            </div>
            <p className="ops-log-live-hint">Live tail re-fetches the last N lines on a timer (not a continuous stream). Increase Lines for more context.</p>
            {logErr && <div className="nginx-msg nginx-msg-err ops-banner"><AlertTriangle size={12} /> {logErr}</div>}
            {logOut && (
              <div className="ops-pre-wrap" ref={logScrollRef}>
                <pre className="ops-pre ops-pre-live">{logOut}</pre>
              </div>
            )}
          </section>

          <section className="ops-section">
            <button type="button" className="ops-accordion-btn" onClick={() => setMgmtOpen((o) => !o)}>
              <Stethoscope size={16} /> Management and diagnostics
              {mgmtOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
            </button>
            {mgmtOpen && (
              <div className="ops-mgmt-body">
                <p className="ops-hint">Reboot is scheduled in about one minute after confirmation. Apt upgrade can take a long time and may restart services.</p>
                <div className="ops-mgmt-btns">
                  <button type="button" className="btn btn-sm" disabled={!pwOk || diagLoading} onClick={() => void runDiagnostics()}>
                    {diagLoading ? <Loader2 size={14} className="spin" /> : <Stethoscope size={14} />}
                    Run diagnostics
                  </button>
                  <button type="button" className="btn btn-sm" disabled={!pwOk || mgmtLoading} onClick={() => void runManage('apt_list_upgradable', '')}>
                    <Package size={14} /> List upgradable
                  </button>
                  <button type="button" className="btn btn-sm" disabled={!pwOk || mgmtLoading} onClick={() => { setModal('apt_update'); setConfirmInput(''); setMgmtErr(null); }}>
                    <Package size={14} /> apt update
                  </button>
                  <button type="button" className="btn btn-sm btn-danger" disabled={!pwOk || mgmtLoading} onClick={() => { setModal('apt_upgrade'); setConfirmInput(''); setMgmtErr(null); }}>
                    <Package size={14} /> apt upgrade
                  </button>
                  <button type="button" className="btn btn-sm btn-danger" disabled={!pwOk || mgmtLoading} onClick={() => { setModal('reboot'); setConfirmInput(''); setMgmtErr(null); }}>
                    <Power size={14} /> Reboot
                  </button>
                </div>
                {mgmtErr && <div className="nginx-msg nginx-msg-err ops-banner">{mgmtErr}</div>}
                {mgmtOut && <pre className="ops-pre">{mgmtOut}</pre>}
                {diagOut && (
                  <>
                    <h3 className="ops-h3"><FileText size={14} /> Diagnostics</h3>
                    <pre className="ops-pre">{diagOut}</pre>
                  </>
                )}
              </div>
            )}
          </section>
        </main>
      </div>

      {modal && (
        <div className="svc-hub-modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) { setModal(null); setConfirmInput(''); } }}>
          <div className="svc-hub-modal ops-modal" role="dialog">
            <div className="svc-hub-modal-hd">
              <h2>
                {modal === 'reboot' && 'Confirm reboot'}
                {modal === 'apt_update' && 'Confirm apt update'}
                {modal === 'apt_upgrade' && 'Confirm apt upgrade'}
              </h2>
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setModal(null); setConfirmInput(''); }}><XCircle size={18} /></button>
            </div>
            <div className="svc-hub-modal-body">
              {modal === 'reboot' && (
                <p className="ops-modal-warn">Server will reboot in about one minute. Type <strong>REBOOT</strong> to confirm.</p>
              )}
              {modal === 'apt_update' && (
                <p>Type <strong>APT_UPDATE</strong> to run <code>apt-get update -y</code>.</p>
              )}
              {modal === 'apt_upgrade' && (
                <p className="ops-modal-warn">This runs a full non-interactive upgrade. Type <strong>APT_UPGRADE</strong> to confirm.</p>
              )}
              <input className="cfg-input" value={confirmInput} onChange={(e) => setConfirmInput(e.target.value)} placeholder="Confirmation text" autoComplete="off" />
              {mgmtErr && modal && <div className="nginx-msg nginx-msg-err">{mgmtErr}</div>}
            </div>
            <div className="svc-hub-modal-ft">
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setModal(null); setConfirmInput(''); setMgmtErr(null); }}>Cancel</button>
              <button type="button" className="btn btn-sm btn-deploy" disabled={mgmtLoading} onClick={submitModal}>
                {mgmtLoading ? <Loader2 size={14} className="spin" /> : null}
                Run
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
